import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ClipboardList, FileText, HelpCircle, Plus, X, Equal, ChevronDown, Pencil } from 'lucide-react';
// Tooltip component — renders into document.body via portal so it's never clipped
// by parent overflow-hidden. Positions above the trigger with a small arrow.
function InfoTooltip({
  text,
  iconClassName = 'text-slate-400'
}: {
  text: string;
  iconClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      top: rect.top - 8,
      left: rect.left + rect.width / 2
    });
  };
  const show = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      updatePosition();
      setOpen(true);
    }, 120);
  };
  const hide = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(false);
  };
  useEffect(() => {
    if (!open) return;
    const onScroll = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  return <>
      <span ref={triggerRef} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide} tabIndex={0} className="inline-flex items-center cursor-help outline-none focus:ring-2 focus:ring-blue-300 rounded-full" aria-label={text} data-id="element-2">
        <HelpCircle size={14} className={iconClassName} data-id="element-3" />
      </span>
      {open && coords && createPortal(<div role="tooltip" style={{
      position: 'fixed',
      top: coords.top,
      left: coords.left,
      transform: 'translate(-50%, -100%)',
      zIndex: 9999,
      pointerEvents: 'none'
    }} className="animate-[tooltip-fade_120ms_ease-out]" data-id="element-4">
            <div className="relative max-w-xs bg-slate-800 text-white text-xs leading-snug px-2.5 py-1.5 rounded shadow-lg" data-id="element-5">
              {text}
              <span className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-slate-800 rotate-45" aria-hidden="true" data-id="element-6" />
            </div>
          </div>, document.body)}
    </>;
}
const fmtBps = (n: number) => Number(n).toFixed(3);
const fmtMoney = (n: number) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
}).format(n);
const fmtCurrencyInput = (val: string | number) => {
  const n = typeof val === 'string' ? parseFloat(val.replace(/[^0-9.-]/g, '')) : val;
  if (isNaN(n) || val === '' || val === null || val === undefined) return '';
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2
  }).format(n);
};
const num = (val: string | number) => {
  if (val === '' || val === '-' || val === null || val === undefined) return 0;
  const raw = typeof val === 'string' ? val.replace(/,/g, '') : val;
  const parsed = typeof raw === 'string' ? parseFloat(raw) : raw;
  return isNaN(parsed) ? 0 : parsed;
};
// Costs are always treated as negative regardless of sign typed.
const negCost = (val: string | number) => -Math.abs(num(val));
// Normalize a cost input on blur: show with leading minus unless zero/empty.
const normalizeCostDisplay = (val: string | number) => {
  if (val === '' || val === null || val === undefined) return '';
  const n = num(val);
  if (n === 0) return '0';
  return `-${Math.abs(n)}`;
};
// Select-all-on-focus handler — lets the user immediately overwrite a default like "0".
const selectOnFocus = (e: React.FocusEvent<HTMLInputElement>) => e.target.select();
const addDays = (dateStr: string, days: number) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  // Fix timezone offset issues by using UTC methods or just adding ms
  const localDate = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
  localDate.setDate(localDate.getDate() + days);
  const y = localDate.getFullYear();
  const m = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const diffDays = (date1: string, date2: string) => {
  if (!date1 || !date2) return 0;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = d1.getTime() - d2.getTime();
  return Math.round(diffTime / (1000 * 60 * 60 * 24));
};
export function PricingDetail() {
  // Top Strip State
  const [loanAmount, setLoanAmount] = useState<string>('513,500');
  const [loanAmountFocused, setLoanAmountFocused] = useState(false);
  const [noteRate, setNoteRate] = useState<string | number>(7.125);
  const [investor, setInvestor] = useState('Aggregator — JPM Correspondent');
  // Lock & Dates State
  const [buyLockDate, setBuyLockDate] = useState('2026-06-05');
  const [buyLockPeriod, setBuyLockPeriod] = useState(30);
  const [sellLockDate, setSellLockDate] = useState('2026-06-05');
  const [sellLockPeriod, setSellLockPeriod] = useState(30);
  const [investorPurchaseDate, setInvestorPurchaseDate] = useState('');
  // General Section State
  const [generalOpen, setGeneralOpen] = useState(true);
  const [generalFields, setGeneralFields] = useState<Record<string, string>>({
    madisonProgramName: '',
    loanNexId: '',
    mersMin: '101841827166764378',
    fedReference: '',
    wireAmount: '',
    wireStatus: '',
    corrInvestorName: '',
    corrInvestorProgramName: '',
    corrInvestorServicer: '',
    warehouseLender: '',
    intendedFundingDate: ''
  });
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const generalFieldsLeft = [{
    key: 'madisonProgramName',
    label: 'Madison Program Name'
  }, {
    key: 'loanNexId',
    label: 'LoanNex Nex ID'
  }, {
    key: 'mersMin',
    label: 'MERS MIN #'
  }, {
    key: 'fedReference',
    label: 'Fed Reference #'
  }, {
    key: 'wireAmount',
    label: 'Wire Amount',
    type: 'decimal'
  }, {
    key: 'wireStatus',
    label: 'Wire Status',
    type: 'select',
    options: ['Needed', 'Requested', 'Completed']
  }];
  const generalFieldsRight = [{
    key: 'corrInvestorName',
    label: 'Corr Investor Name',
    type: 'select',
    options: ['UWM', 'eRESI', 'ARC Home Loans', 'Champion', 'Vista Point', 'Onslow', 'Foundation Mortgage', 'Other']
  }, {
    key: 'corrInvestorProgramName',
    label: 'Corr Investor Program Name'
  }, {
    key: 'corrInvestorServicer',
    label: 'Corr Investor Servicer'
  }, {
    key: 'warehouseLender',
    label: 'Warehouse Lender'
  }, {
    key: 'intendedFundingDate',
    label: 'Intended Funding Date',
    type: 'date'
  }];
  // Computed Lock Expirations
  const buyExpiration = addDays(buyLockDate, buyLockPeriod);
  const sellExpiration = addDays(sellLockDate, sellLockPeriod);
  const lockPaddingDays = diffDays(sellExpiration, buyExpiration);
  // Buy Side State
  const [buyPrice, setBuyPrice] = useState<string | number>(100.124);
  const [buyAdj, setBuyAdj] = useState<{
    label: string;
    value: string | number;
  }[]>([{
    label: '',
    value: 0
  }, {
    label: '',
    value: 0
  }, {
    label: '',
    value: 0
  }, {
    label: '',
    value: 0
  }]);
  const [buyCosts, setBuyCosts] = useState({
    lockExtension: 0 as string | number,
    lenderCredit: 0 as string | number,
    priceConcession: 0 as string | number
  });
  const [buyPoints, setBuyPoints] = useState({
    discount: 1 as string | number,
    origination: 0 as string | number
  });
  // Sell Side State
  // Sell price is now fully independent of buy price — manual entry only.
  const [sellPrice, setSellPrice] = useState<string | number>(100.124);
  // Each sell adjustment tracks its own label, plus a flag indicating whether
  // the user has manually edited it (which detaches it from the linked buy label).
  const [sellAdj, setSellAdj] = useState<{
    id: number;
    label: string;
    labelManuallyEdited: boolean;
    value: string | number;
  }[]>([{
    id: 1,
    label: '',
    labelManuallyEdited: false,
    value: 0.4
  }, {
    id: 2,
    label: '',
    labelManuallyEdited: false,
    value: 0.53
  }, {
    id: 3,
    label: '',
    labelManuallyEdited: false,
    value: 0
  }, {
    id: 4,
    label: '',
    labelManuallyEdited: false,
    value: 0
  }]);
  const [sellLockExtension, setSellLockExtension] = useState<string | number>(0);
  // Loan amount derived
  const loanAmtNum = num(loanAmount);
  // Buy Calculations — costs are always subtracted via negCost (always negative)
  const buyTotalAdj = buyAdj.reduce((acc, a) => acc + num(a.value), 0) + negCost(buyCosts.lockExtension) + negCost(buyCosts.lenderCredit) + negCost(buyCosts.priceConcession);
  const netBuyPrice = num(buyPrice) + buyTotalAdj;
  const loCompBps = netBuyPrice - 100 + num(buyPoints.discount) + num(buyPoints.origination);
  const loCompDollars = loCompBps / 100 * loanAmtNum;
  // Sell Calculations — costs always negative; sell-side Lender Credit mirrors buy.
  const sellTotalAdj = sellAdj.reduce((acc, a) => acc + num(a.value), 0) + negCost(sellLockExtension) + negCost(buyCosts.lenderCredit);
  const netSellPrice = num(sellPrice) + sellTotalAdj;
  const marginBps = netSellPrice - netBuyPrice;
  const marginDollars = marginBps / 100 * loanAmtNum;
  // Effective label shown on a sell adjustment row — if the user has edited it,
  // use the override; otherwise fall back to the linked buy adjustment label.
  const effectiveSellLabel = (index: number) => {
    const sell = sellAdj[index];
    if (!sell) return '';
    if (sell.labelManuallyEdited) return sell.label;
    const buy = buyAdj[index];
    return buy?.label ?? '';
  };
  // Handlers
  const handleBuyAdjChange = (index: number, field: 'label' | 'value', val: string) => {
    const newBuy = [...buyAdj];
    newBuy[index] = {
      ...newBuy[index],
      [field]: val
    };
    setBuyAdj(newBuy);
    // If editing a buy label, propagate to the same-index sell adjustment
    // unless the user has already manually edited that sell label.
    if (field === 'label') {
      setSellAdj(prev => prev.map((s, i) => i === index && !s.labelManuallyEdited ? {
        ...s,
        label: val
      } : s));
    }
  };
  const handleSellAdjChange = (id: number, field: 'label' | 'value', val: string) => {
    setSellAdj(sellAdj.map(a => {
      if (a.id !== id) return a;
      if (field === 'label') {
        return {
          ...a,
          label: val,
          labelManuallyEdited: true
        };
      }
      return {
        ...a,
        value: val
      };
    }));
  };
  const addSellAdj = () => {
    const newId = Math.max(0, ...sellAdj.map(a => a.id)) + 1;
    setSellAdj([...sellAdj, {
      id: newId,
      label: '',
      labelManuallyEdited: true,
      value: 0
    }]);
  };
  const removeSellAdj = (id: number) => {
    setSellAdj(sellAdj.filter(a => a.id !== id));
  };
  const addBuyAdj = () => {
    setBuyAdj([...buyAdj, {
      label: '',
      value: 0
    }]);
  };
  const removeBuyAdj = (index: number) => {
    setBuyAdj(buyAdj.filter((_, i) => i !== index));
  };
  // Max rows so both sides render the same count (shorter side fills with spacers).
  const maxAdjRows = Math.max(buyAdj.length, sellAdj.length);
  // Cost input blur — normalize to negative display ("0.5" → "-0.5").
  const onCostBlur = (setter: (v: string) => void) => (e: React.FocusEvent<HTMLInputElement>) => {
    setter(normalizeCostDisplay(e.target.value));
  };
  // Loan amount input handlers — show formatted with commas when blurred,
  // raw digits when focused for easy editing.
  const onLoanFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setLoanAmountFocused(true);
    // strip commas for raw editing
    const raw = String(loanAmount).replace(/,/g, '');
    setLoanAmount(raw);
    requestAnimationFrame(() => e.target.select());
  };
  const onLoanBlur = () => {
    setLoanAmountFocused(false);
    const n = num(loanAmount);
    setLoanAmount(n === 0 && loanAmount === '' ? '' : fmtCurrencyInput(n));
  };
  // Shared UI primitives
  // Spacer row used to keep buy/sell sections vertically aligned.
  // Height matches a real input row (input ~30px + py-1.5 → 42px).
  const SpacerRow = () => <div className="h-[42px] border-b border-slate-100" aria-hidden="true" data-id="element-7" />;
  const BadgeIN = () => null;
  const BadgeMirror = () => null;
  const SectionHeader = ({
    title,
    badge
  }: {
    title: string;
    badge: string;
  }) => <div className="bg-slate-200 text-slate-700 text-[10px] font-bold tracking-wider uppercase py-1.5 px-4 flex justify-between items-center border-y border-slate-300" data-id="element-8">
      <span data-id="element-9">{title}</span>
      <span className="bg-slate-300 text-slate-700 px-1.5 py-0.5 rounded" data-id="element-10">
        {badge}
      </span>
    </div>;
  const setGeneralValue = (key: string, value: string) => setGeneralFields(prev => ({
    ...prev,
    [key]: value
  }));
  // Plain render function (NOT a nested component) so the edit input keeps
  // focus while typing instead of remounting on each keystroke.
  const renderGeneralField = ({
    key,
    label,
    type = 'text',
    options
  }: {
    key: string;
    label: string;
    type?: 'text' | 'date' | 'select' | 'decimal';
    options?: string[];
  }) => {
    const value = generalFields[key] ?? '';
    const isEditing = editingKey === key;
    const editClasses = 'w-full text-sm text-right border border-blue-400 rounded px-2 py-0.5 focus:ring-1 focus:ring-blue-500 outline-none';
    return <div key={key} className="flex items-center gap-3 px-1 border-b border-slate-300 group h-12" data-id="element-11">
        <span className="text-xs font-bold text-slate-600 w-40 shrink-0 leading-tight" data-id="element-12">
          {label}
        </span>
        <div className="flex-1 min-w-0" data-id="element-13">
          {isEditing ? type === 'select' ? <select autoFocus value={value} onChange={e => setGeneralValue(key, e.target.value)} onBlur={() => setEditingKey(null)} className={`${editClasses} bg-white`} data-id="element-14">
                <option value="" data-id="element-15">—</option>
                {options?.map(opt => <option key={opt} value={opt} data-id="element-16">
                    {opt}
                  </option>)}
              </select> : <input autoFocus type={type === 'date' ? 'date' : 'text'} inputMode={type === 'decimal' ? 'decimal' : undefined} value={value} onChange={e => setGeneralValue(key, e.target.value)} onBlur={() => setEditingKey(null)} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === 'Escape') {
            setEditingKey(null);
          }
        }} className={editClasses} data-id="element-17" /> : <span className="block text-sm text-slate-800 truncate text-right" data-id="element-18">
              {value || <span className="text-slate-400 italic" data-id="element-19">—</span>}
            </span>}
        </div>
        <button onClick={() => setEditingKey(isEditing ? null : key)} className={`text-slate-400 hover:text-blue-600 transition-all shrink-0 ${isEditing ? 'text-blue-600' : 'opacity-0 group-hover:opacity-100'}`} aria-label={`Edit ${label}`} data-id="element-20">
          <Pencil size={14} data-id="element-21" />
        </button>
      </div>;
  };
  return <div className="print-card w-full max-w-[1400px] mx-auto bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col font-sans" data-id="element-22">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200" data-id="element-23">
        <div className="flex items-center gap-3" data-id="element-24">
          <div className="bg-blue-100 p-1.5 rounded text-blue-600" data-id="element-25">
            <ClipboardList size={18} data-id="element-26" />
          </div>
          <div data-id="element-27">
            <h1 className="text-base font-bold text-slate-800 leading-tight" data-id="element-28">
              Buy Side / Sell Side Pricing Detail
            </h1>
            <p className="text-xs text-slate-500" data-id="element-29">
              Live · 10:47 AM CT · Rate sheet 2026-05-12-am-r3
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-30">
          <button onClick={() => window.print()} className="no-print flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-full hover:bg-slate-50 transition-colors" data-id="element-31">
            <FileText size={14} data-id="element-32" /> Export PDF
          </button>
        </div>
      </div>

      {/* Top Section */}
      <div className="bg-slate-50 border-b border-slate-200" data-id="element-33">
        {/* Key Inputs */}
        <div className="grid grid-cols-3 divide-x divide-slate-200 border-b border-slate-200" data-id="element-34">
          <div className="p-4" data-id="element-35">
            <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-36">
              Loan Amount <span className="text-red-500" data-id="element-37">*</span>
            </label>
            <div className="relative" data-id="element-38">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm" data-id="element-39">
                $
              </span>
              <input type="text" readOnly value={loanAmount} className="w-full pl-7 pr-3 py-1.5 border border-transparent bg-slate-100 rounded text-sm text-slate-700 outline-none cursor-default tabular-nums" data-id="element-40" />
            </div>
          </div>
          <div className="p-4" data-id="element-41">
            <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-42">
              Current Note Rate <span className="text-red-500" data-id="element-43">*</span>
            </label>
            <div className="relative" data-id="element-44">
              <input type="text" readOnly value={noteRate} className="w-full pl-3 pr-7 py-1.5 border border-transparent bg-slate-100 rounded text-sm text-slate-700 outline-none cursor-default" data-id="element-45" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm" data-id="element-46">
                %
              </span>
            </div>
          </div>
          <div className="p-4" data-id="element-47">
            <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-48">
              Investor / Channel
            </label>
            <select value={investor} onChange={e => setInvestor(e.target.value)} className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all bg-white" data-id="element-49">
              <option data-id="element-50">Aggregator — JPM Correspondent</option>
              <option data-id="element-51">Fannie Mae Direct</option>
              <option data-id="element-52">Freddie Mac Direct</option>
            </select>
          </div>
        </div>

        {/* Lock & Dates */}
        <div className="p-4 border-b border-slate-200" data-id="element-53">
          <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-54">
            Lock & Dates
          </h3>
          <div className="grid grid-cols-2 gap-6" data-id="element-55">
            {/* Buy Side Lock */}
            <div className="bg-white border border-blue-200 rounded-lg overflow-hidden shadow-sm" data-id="element-56">
              <div className="bg-blue-100 px-3 py-1.5 border-b border-blue-300" data-id="element-57">
                <span className="text-[10px] font-bold text-blue-800 uppercase tracking-wide" data-id="element-58">
                  Buy Side Lock
                </span>
              </div>
              <div className="p-3 grid grid-cols-3 gap-3" data-id="element-59">
                <div data-id="element-60">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5" data-id="element-61">
                    Lock Date
                  </label>
                  <input type="date" value={buyLockDate} onChange={e => setBuyLockDate(e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all" data-id="element-62" />
                </div>
                <div data-id="element-63">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5" data-id="element-64">
                    Period
                  </label>
                  <input type="number" min={0} value={buyLockPeriod} onFocus={selectOnFocus} onChange={e => setBuyLockPeriod(Number(e.target.value))} className="w-full px-2 py-1 border border-slate-300 rounded text-sm tabular-nums focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all" data-id="element-65" />
                </div>
                <div data-id="element-66">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5" data-id="element-67">
                    Expiration
                  </label>
                  <div className="relative" data-id="element-68">
                    <Equal size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-69" />
                    <input readOnly value={buyExpiration} className="w-full pl-6 pr-2 py-1 border border-transparent bg-slate-100 rounded text-sm text-slate-600 outline-none" data-id="element-70" />
                  </div>
                </div>
              </div>
            </div>

            {/* Sell Side Lock */}
            <div className="bg-white border border-emerald-200 rounded-lg overflow-hidden shadow-sm" data-id="element-71">
              <div className="bg-emerald-100 px-3 py-1.5 border-b border-emerald-300" data-id="element-72">
                <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wide" data-id="element-73">
                  Sell Side Lock
                </span>
              </div>
              <div className="p-3 grid grid-cols-3 gap-3" data-id="element-74">
                <div data-id="element-75">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5" data-id="element-76">
                    Lock Date
                  </label>
                  <input type="date" value={sellLockDate} onChange={e => setSellLockDate(e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all" data-id="element-77" />
                </div>
                <div data-id="element-78">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5" data-id="element-79">
                    Period
                  </label>
                  <input type="number" min={0} value={sellLockPeriod} onFocus={selectOnFocus} onChange={e => setSellLockPeriod(Number(e.target.value))} className="w-full px-2 py-1 border border-slate-300 rounded text-sm tabular-nums focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all" data-id="element-80" />
                </div>
                <div data-id="element-81">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5" data-id="element-82">
                    Expiration
                  </label>
                  <div className="relative" data-id="element-83">
                    <Equal size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-84" />
                    <input readOnly value={sellExpiration} className="w-full pl-6 pr-2 py-1 border border-transparent bg-slate-100 rounded text-sm text-slate-600 outline-none" data-id="element-85" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-8 px-1" data-id="element-86">
            <div className="flex items-center gap-2" data-id="element-87">
              <span className="text-sm font-medium text-slate-600" data-id="element-88">
                Lock Period Padding:
              </span>
              <span className={`text-sm font-bold ${lockPaddingDays < 0 ? 'text-rose-600' : 'text-slate-800'}`} data-id="element-89">
                {lockPaddingDays > 0 ? '+' : ''}
                {lockPaddingDays} days
              </span>
              <InfoTooltip text="Days between the sell-side and buy-side lock expirations." data-id="element-90" />
            </div>
            <div className="flex items-center gap-2" data-id="element-91">
              <label className="text-sm font-medium text-slate-600" data-id="element-92">
                Investor Purchase Date:
              </label>
              <input type="date" value={investorPurchaseDate} onChange={e => setInvestorPurchaseDate(e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" data-id="element-93" />
            </div>
          </div>
        </div>

        {/* General Section (Collapsible) */}
        <div data-id="element-94">
          <button onClick={() => setGeneralOpen(!generalOpen)} className="w-full flex items-center gap-2 px-4 py-2 bg-slate-200 hover:bg-slate-300 transition-colors text-sm font-semibold text-slate-800 border-b border-slate-300" data-id="element-95">
            <ChevronDown className={`transition-transform ${generalOpen ? '' : '-rotate-90'}`} size={16} data-id="element-96" />
            General
          </button>
          {generalOpen && <div className="bg-white flex divide-x divide-slate-300 border-b border-slate-200" data-id="element-97">
              <div className="flex-1 flex flex-col px-4 py-4 min-w-0" data-id="element-98">
                {generalFieldsLeft.map(renderGeneralField)}
              </div>
              <div className="flex-1 flex flex-col px-4 py-4 min-w-0" data-id="element-99">
                {generalFieldsRight.map(renderGeneralField)}
              </div>
            </div>}
        </div>
      </div>

      {/* Two-Column Grid */}
      <div className="flex flex-1 divide-x divide-slate-300 bg-white" data-id="element-100">
        {/* ================= BUY SIDE ================= */}
        <div className="flex-1 flex flex-col" data-id="element-101">
          <div className="bg-blue-200 py-2 px-4 flex justify-between items-center border-b border-blue-300" data-id="element-102">
            <div className="flex items-center gap-2" data-id="element-103">
              <span className="text-blue-900 font-bold text-sm uppercase tracking-wide" data-id="element-104">
                Buy Side
              </span>
            </div>
            <span className="text-[10px] font-bold text-blue-800 uppercase tracking-wider" data-id="element-105">
              From Lender
            </span>
          </div>
          {/* Buy Side Price */}
          <div className="flex items-center justify-between py-2 px-4 border-b border-slate-100" data-id="element-106">
            <div className="flex items-center gap-2" data-id="element-107">
              <BadgeIN data-id="element-108" />
              <span className="text-sm text-slate-700" data-id="element-109">Buy Side Price</span>
              <InfoTooltip text="Base price from the lender before adjustments" data-id="element-110" />
            </div>
            <input type="number" step="0.001" value={buyPrice} onFocus={selectOnFocus} onChange={e => setBuyPrice(e.target.value)} className="w-28 text-right tabular-nums border border-slate-300 rounded px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all" data-id="element-111" />
          </div>
          <SectionHeader title="Costs" badge="ALWAYS NEGATIVE" data-id="element-112" />
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100" data-id="element-113">
            <div className="flex items-center gap-2" data-id="element-114">
              <BadgeIN data-id="element-115" />
              <span className="text-sm text-slate-700" data-id="element-116">Lock Extension</span>
              <InfoTooltip text="Cost to extend the rate lock. Always treated as negative." data-id="element-117" />
            </div>
            <input type="text" inputMode="decimal" value={buyCosts.lockExtension} onFocus={selectOnFocus} onChange={e => setBuyCosts({
            ...buyCosts,
            lockExtension: e.target.value
          })} onBlur={onCostBlur(v => setBuyCosts(prev => ({
            ...prev,
            lockExtension: v
          })))} className="w-24 text-right tabular-nums border border-rose-200 bg-rose-50 text-rose-900 rounded px-2 py-1 text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none transition-all" data-id="element-118" />
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100" data-id="element-119">
            <div className="flex items-center gap-2" data-id="element-120">
              <BadgeIN data-id="element-121" />
              <span className="text-sm text-slate-700" data-id="element-122">Lender Credit</span>
              <InfoTooltip text="Credit provided by the lender. Always treated as negative." data-id="element-123" />
            </div>
            <input type="text" inputMode="decimal" value={buyCosts.lenderCredit} onFocus={selectOnFocus} onChange={e => setBuyCosts({
            ...buyCosts,
            lenderCredit: e.target.value
          })} onBlur={onCostBlur(v => setBuyCosts(prev => ({
            ...prev,
            lenderCredit: v
          })))} className="w-24 text-right tabular-nums border border-rose-200 bg-rose-50 text-rose-900 rounded px-2 py-1 text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none transition-all" data-id="element-124" />
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100" data-id="element-125">
            <div className="flex items-center gap-2" data-id="element-126">
              <BadgeIN data-id="element-127" />
              <span className="text-sm text-slate-700" data-id="element-128">
                Price Concession / Special Incentive
              </span>
              <InfoTooltip text="Additional price concessions. Always treated as negative." data-id="element-129" />
            </div>
            <input type="text" inputMode="decimal" value={buyCosts.priceConcession} onFocus={selectOnFocus} onChange={e => setBuyCosts({
            ...buyCosts,
            priceConcession: e.target.value
          })} onBlur={onCostBlur(v => setBuyCosts(prev => ({
            ...prev,
            priceConcession: v
          })))} className="w-24 text-right tabular-nums border border-rose-200 bg-rose-50 text-rose-900 rounded px-2 py-1 text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none transition-all" data-id="element-130" />
          </div>
          <SectionHeader title="Adjustments" badge="BPS" data-id="element-131" />
          {Array.from({
          length: maxAdjRows
        }).map((_, i) => {
          const adj = buyAdj[i];
          if (!adj) {
            // Spacer row — keeps buy/sell tabs aligned when sell has more rows
            return <SpacerRow key={`buy-spacer-${i}`} data-id="element-132" />;
          }
          return <div key={i} className="flex items-center group py-1.5 px-4 border-b border-slate-100 hover:bg-slate-50 transition-colors" data-id="element-133">
                <button onClick={() => removeBuyAdj(i)} className="mr-2 shrink-0 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all" title="Remove adjustment" data-id="element-134">
                  <X size={16} data-id="element-135" />
                </button>
                <input type="text" value={adj.label} onChange={e => handleBuyAdjChange(i, 'label', e.target.value)} placeholder="Adjustment label..." className="text-sm text-slate-600 bg-transparent border-none focus:ring-0 w-full outline-none placeholder:italic placeholder:text-slate-400" data-id="element-136" />
                <input type="text" inputMode="decimal" value={adj.value} onFocus={selectOnFocus} onChange={e => handleBuyAdjChange(i, 'value', e.target.value)} className="w-24 text-right tabular-nums border border-slate-300 rounded px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all bg-white" data-id="element-137" />
              </div>;
        })}
          <div className="py-2 px-4 border-b border-slate-100 flex items-center justify-between" data-id="element-138">
            <button onClick={addBuyAdj} className="flex items-center gap-1.5 text-xs font-medium text-blue-600 border border-dashed border-blue-300 bg-blue-50/50 hover:bg-blue-50 px-3 py-1.5 rounded transition-colors" data-id="element-139">
              <Plus size={14} data-id="element-140" /> Add buy-side adjustment
            </button>
            <span className="text-[11px] text-slate-400" data-id="element-141">
              {buyAdj.length} adjustments
            </span>
          </div>
          <div className="flex items-center justify-between py-2.5 px-4 border-t-2 border-slate-100" data-id="element-142">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wide" data-id="element-143">
              Total Adjustments
            </span>
            <span className="text-sm font-bold text-slate-800 tabular-nums" data-id="element-144">
              {fmtBps(buyTotalAdj)}
            </span>
          </div>
          <div className="flex items-center justify-between py-3 px-4 border-t-2 border-blue-300 bg-blue-100" data-id="element-145">
            <div className="flex items-center gap-2" data-id="element-146">
              <span className="text-sm font-bold text-blue-900 uppercase tracking-wide" data-id="element-147">
                Net Buy Price
              </span>
              <InfoTooltip text="Buy Side Price + Total Adjustments. The effective price we pay the lender." iconClassName="text-blue-400" data-id="element-148" />
            </div>
            <span className="text-base font-bold text-slate-800 tabular-nums" data-id="element-149">
              {fmtBps(netBuyPrice)}
            </span>
          </div>
          <SectionHeader title="Borrower-Paid Points" badge="BPS" data-id="element-150" />
          {/* Spacer row — matches sell "Buy Side Price Concession" */}
          <SpacerRow data-id="element-151" />
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100" data-id="element-152">
            <div className="flex items-center gap-2" data-id="element-153">
              <BadgeIN data-id="element-154" />
              <span className="text-sm text-slate-700" data-id="element-155">Discount Points</span>
              <InfoTooltip text="Points paid by the borrower to buy down the rate. Adds to LO Compensation." data-id="element-156" />
            </div>
            <input type="number" step="0.001" value={buyPoints.discount} onFocus={selectOnFocus} onChange={e => setBuyPoints({
            ...buyPoints,
            discount: e.target.value
          })} className="w-24 text-right tabular-nums border border-slate-300 rounded px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all" data-id="element-157" />
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100" data-id="element-158">
            <div className="flex items-center gap-2" data-id="element-159">
              <BadgeIN data-id="element-160" />
              <span className="text-sm text-slate-700" data-id="element-161">Origination Points</span>
              <InfoTooltip text="Points paid by the borrower for loan origination. Adds to LO Compensation." data-id="element-162" />
            </div>
            <input type="number" step="0.001" value={buyPoints.origination} onFocus={selectOnFocus} onChange={e => setBuyPoints({
            ...buyPoints,
            origination: e.target.value
          })} className="w-24 text-right tabular-nums border border-slate-300 rounded px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all" data-id="element-163" />
          </div>
          {/* Spacer row — matches sell "LO Compensation (cost)" so LO Comp ↔ Margin/GOS align */}
          <SpacerRow data-id="element-164" />
          <div className="flex items-center justify-between py-3 px-4 border-t-2 border-slate-100 mt-auto" data-id="element-165">
            <div className="flex items-center gap-2" data-id="element-166">
              <span className="text-sm text-slate-700" data-id="element-167">LO Compensation</span>
              <InfoTooltip text="Net Buy Price − 100 + Discount Points + Origination Points. The loan officer's compensation in bps." data-id="element-168" />
            </div>
            <span className="text-sm font-bold text-slate-800 tabular-nums" data-id="element-169">
              {fmtBps(loCompBps)}
            </span>
          </div>
          <div className="flex items-center justify-between py-4 px-4 border-t-2 border-blue-300 bg-gradient-to-r from-blue-200 to-blue-100" data-id="element-170">
            <span className="text-sm font-bold text-blue-900 uppercase tracking-wide" data-id="element-171">
              LO Comp ($)
            </span>
            <span className="text-2xl font-bold text-blue-800 tabular-nums" data-id="element-172">
              {fmtMoney(loCompDollars)}
            </span>
          </div>
        </div>

        {/* ================= SELL SIDE ================= */}
        <div className="flex-1 flex flex-col" data-id="element-173">
          <div className="bg-emerald-200 py-2 px-4 flex justify-between items-center border-b border-emerald-300" data-id="element-174">
            <div className="flex items-center gap-2" data-id="element-175">
              <span className="text-emerald-900 font-bold text-sm uppercase tracking-wide" data-id="element-176">
                Sell Side
              </span>
            </div>
            <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider" data-id="element-177">
              To Investor
            </span>
          </div>

          {/* Sell Side Price — independent of buy price */}
          <div className="flex items-center justify-between py-2 px-4 border-b border-slate-100" data-id="element-178">
            <div className="flex items-center gap-2" data-id="element-179">
              <BadgeIN data-id="element-180" />
              <span className="text-sm text-slate-700" data-id="element-181">Sell Side Price</span>
              <InfoTooltip text="Price the investor pays us. Enter manually — not linked to Buy Side Price." data-id="element-182" />
            </div>
            <input type="number" step="0.001" value={sellPrice} onFocus={selectOnFocus} onChange={e => setSellPrice(e.target.value)} className="w-28 text-right tabular-nums border border-slate-300 rounded px-2 py-1 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all" data-id="element-183" />
          </div>

          <SectionHeader title="Costs" badge="ALWAYS NEGATIVE" data-id="element-184" />

          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100" data-id="element-185">
            <div className="flex items-center gap-2" data-id="element-186">
              <BadgeIN data-id="element-187" />
              <span className="text-sm text-slate-700" data-id="element-188">Lock Extension</span>
              <InfoTooltip text="Cost to extend the lock on the sell side. Always treated as negative." data-id="element-189" />
            </div>
            <input type="text" inputMode="decimal" value={sellLockExtension} onFocus={selectOnFocus} onChange={e => setSellLockExtension(e.target.value)} onBlur={onCostBlur(v => setSellLockExtension(v))} className="w-24 text-right tabular-nums border border-rose-200 bg-rose-50 text-rose-900 rounded px-2 py-1 text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none transition-all" data-id="element-190" />
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100 bg-slate-50/50" data-id="element-191">
            <div className="flex items-center gap-2" data-id="element-192">
              <BadgeMirror data-id="element-193" />
              <span className="text-sm text-slate-600" data-id="element-194">Lender Credit</span>
              <InfoTooltip text="Mirrored from Buy Side Lender Credit — kept in sync automatically." data-id="element-195" />
            </div>
            <div className="relative w-24" data-id="element-196">
              <Equal size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-197" />
              <input readOnly value={num(buyCosts.lenderCredit) === 0 ? '0' : fmtBps(negCost(buyCosts.lenderCredit))} className="w-full text-right tabular-nums border border-transparent bg-slate-200/50 rounded px-2 py-1 text-sm text-slate-600 pl-6 outline-none" data-id="element-198" />
            </div>
          </div>
          {/* Spacer row — matches buy "Price Concession" so Adjustments headers align */}
          <SpacerRow data-id="element-199" />

          <SectionHeader title="Adjustments" badge="BPS" data-id="element-200" />

          {Array.from({
          length: maxAdjRows
        }).map((_, i) => {
          const adj = sellAdj[i];
          if (!adj) {
            // Spacer row — keeps buy/sell tabs aligned when buy has more rows
            return <SpacerRow key={`sell-spacer-${i}`} data-id="element-201" />;
          }
          const isLinked = i < buyAdj.length;
          const displayLabel = isLinked ? effectiveSellLabel(i) : adj.label;
          return <div key={adj.id} className="flex items-center group py-1.5 px-4 border-b border-slate-100 hover:bg-slate-50 transition-colors" data-id="element-202">
                <button onClick={() => removeSellAdj(adj.id)} className="mr-2 shrink-0 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all" title="Remove adjustment" data-id="element-203">
                  <X size={16} data-id="element-204" />
                </button>
                <input type="text" value={displayLabel} onChange={e => handleSellAdjChange(adj.id, 'label', e.target.value)} className="text-sm text-slate-600 bg-transparent border-none focus:ring-0 w-full outline-none placeholder:italic placeholder:text-slate-400" placeholder="Adjustment label..." data-id="element-205" />
                <input type="text" inputMode="decimal" value={adj.value} onFocus={selectOnFocus} onChange={e => handleSellAdjChange(adj.id, 'value', e.target.value)} className="w-24 text-right tabular-nums border border-slate-300 rounded px-2 py-1 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all bg-white" data-id="element-206" />
              </div>;
        })}

          <div className="py-2 px-4 border-b border-slate-100 flex items-center justify-between" data-id="element-207">
            <button onClick={addSellAdj} className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 border border-dashed border-emerald-300 bg-emerald-50/50 hover:bg-emerald-50 px-3 py-1.5 rounded transition-colors" data-id="element-208">
              <Plus size={14} data-id="element-209" /> Add sell-side only adjustment
            </button>
            <span className="text-[11px] text-slate-400" data-id="element-210">
              {sellAdj.length} adjustments
            </span>
          </div>

          <div className="flex items-center justify-between py-2.5 px-4 border-t-2 border-slate-100" data-id="element-211">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wide" data-id="element-212">
              Total Adjustments
            </span>
            <span className="text-sm font-bold text-slate-800 tabular-nums" data-id="element-213">
              {fmtBps(sellTotalAdj)}
            </span>
          </div>

          <div className="flex items-center justify-between py-3 px-4 border-t-2 border-emerald-300 bg-emerald-100" data-id="element-214">
            <div className="flex items-center gap-2" data-id="element-215">
              <span className="text-sm font-bold text-emerald-900 uppercase tracking-wide" data-id="element-216">
                Net Sell Price
              </span>
              <InfoTooltip text="Sell Side Price + Total Adjustments. The effective price the investor pays us." iconClassName="text-emerald-400" data-id="element-217" />
            </div>
            <span className="text-base font-bold text-slate-800 tabular-nums" data-id="element-218">
              {fmtBps(netSellPrice)}
            </span>
          </div>

          <SectionHeader title="Sell-Side Cost Stack" badge="BPS" data-id="element-219" />

          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100 bg-slate-50/50" data-id="element-220">
            <div className="flex items-center gap-2" data-id="element-221">
              <BadgeMirror data-id="element-222" />
              <span className="text-sm text-slate-600" data-id="element-223">
                Buy Side Price Concession
              </span>
              <InfoTooltip text="Mirrored from Buy Side Price Concession / Special Incentive." data-id="element-224" />
            </div>
            <div className="relative w-24" data-id="element-225">
              <Equal size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-226" />
              <input readOnly value={num(buyCosts.priceConcession) === 0 ? '0' : fmtBps(negCost(buyCosts.priceConcession))} className="w-full text-right tabular-nums border border-transparent bg-slate-200/50 rounded px-2 py-1 text-sm text-slate-600 pl-6 outline-none" data-id="element-227" />
            </div>
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100 bg-slate-50/50" data-id="element-228">
            <div className="flex items-center gap-2" data-id="element-229">
              <BadgeMirror data-id="element-230" />
              <span className="text-sm text-slate-600" data-id="element-231">Discount Points</span>
            </div>
            <div className="relative w-24" data-id="element-232">
              <Equal size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-233" />
              <input readOnly value={num(buyPoints.discount) === 0 ? '0' : fmtBps(num(buyPoints.discount))} className="w-full text-right tabular-nums border border-transparent bg-slate-200/50 rounded px-2 py-1 text-sm text-slate-600 pl-6 outline-none" data-id="element-234" />
            </div>
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100 bg-slate-50/50" data-id="element-235">
            <div className="flex items-center gap-2" data-id="element-236">
              <BadgeMirror data-id="element-237" />
              <span className="text-sm text-slate-600" data-id="element-238">Origination Points</span>
            </div>
            <div className="relative w-24" data-id="element-239">
              <Equal size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-240" />
              <input readOnly value={num(buyPoints.origination) === 0 ? '0' : fmtBps(num(buyPoints.origination))} className="w-full text-right tabular-nums border border-transparent bg-slate-200/50 rounded px-2 py-1 text-sm text-slate-600 pl-6 outline-none" data-id="element-241" />
            </div>
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100 bg-slate-50/50" data-id="element-242">
            <div className="flex items-center gap-2" data-id="element-243">
              <BadgeMirror data-id="element-244" />
              <span className="text-sm text-slate-600" data-id="element-245">
                LO Compensation (cost)
              </span>
              <InfoTooltip text="Negative of Buy Side LO Compensation — the cost to us of paying the loan officer." data-id="element-246" />
            </div>
            <div className="relative w-24" data-id="element-247">
              <Equal size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-248" />
              <input readOnly value={loCompBps === 0 ? '0' : fmtBps(-loCompBps)} className="w-full text-right tabular-nums border border-transparent bg-slate-200/50 rounded px-2 py-1 text-sm text-slate-600 pl-6 outline-none" data-id="element-249" />
            </div>
          </div>

          <div className="flex items-center justify-between py-3 px-4 border-t-2 border-slate-100 mt-auto" data-id="element-250">
            <div className="flex items-center gap-2" data-id="element-251">
              <span className="text-sm text-slate-700" data-id="element-252">Margin / GOS</span>
              <InfoTooltip text="Net Sell Price − Net Buy Price. The gain on sale in basis points." data-id="element-253" />
            </div>
            <span className="text-sm font-bold text-slate-800 tabular-nums" data-id="element-254">
              {fmtBps(marginBps)}
            </span>
          </div>

          <div className="flex items-center justify-between py-4 px-4 border-t-2 border-emerald-300 bg-gradient-to-r from-emerald-100 to-emerald-200" data-id="element-255">
            <span className="text-sm font-bold text-emerald-900 uppercase tracking-wide" data-id="element-256">
              Margin / GOS ($)
            </span>
            <span className="text-2xl font-bold text-emerald-800 tabular-nums" data-id="element-257">
              {fmtMoney(marginDollars)}
            </span>
          </div>
        </div>
      </div>
    </div>;
}